import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminSettingsApi } from '../api/operations';
import { adminFilesApi } from '../api/sales';
import type { EmailTemplateDto, EmailTemplateUpsertDto } from '../api/types';
import { PageHeader } from '../components/Shell';
import {
  ConfirmDialog,
  ErrorState,
  Field,
  Modal,
  PageLoader,
  Spinner,
} from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/format';
import { P } from '../lib/permissions';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code2,
  Columns,
  Eye,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Info,
  Italic,
  LayoutTemplate,
  Link2,
  List,
  ListOrdered,
  Mail,
  Minus,
  Monitor,
  Palette,
  Plus,
  RotateCcw,
  Save,
  Search,
  Send,
  Smartphone,
  Sparkles,
  SquareMousePointer,
  Strikethrough,
  Table,
  Trash2,
  Type,
  Underline,
  Upload,
  Wand2,
} from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  Degisken (Placeholder) Sozlugu: Turkce aciklama + Ornek deger
 * ------------------------------------------------------------------ */

interface PlaceholderMeta {
  label: string;
  sample: string;
}

const PLACEHOLDER_DICTIONARY: Record<string, PlaceholderMeta> = {
  StoreName: { label: 'Mağaza Adı', sample: 'Hurmahane' },
  StorefrontUrl: { label: 'Mağaza Web Adresi', sample: 'https://hurmahane.com' },
  SupportEmail: { label: 'Destek E-Postası', sample: 'info@hurmahane.com' },
  SupportPhone: { label: 'Müşteri Hizmetleri Telefonu', sample: '0 (544) 343 49 97' },
  CompanyAddress: { label: 'Şirket Adresi', sample: 'Konya Selçuklu' },
  LogoUrl: { label: 'Mağaza Logo URL', sample: 'https://hurmahane.com/logo.png' },
  Year: { label: 'Bulunulan Yıl', sample: new Date().getFullYear().toString() },
  Content: {
    label: 'Şablon Ana İçeriği (Yalnızca Ortak Çerçevede)',
    sample:
      '<h1 style="margin:0 0 14px 0;font-size:22px;color:#0f172a;">Örnek E-Posta Başlığı 👋</h1><p style="margin:0;color:#475569;font-size:15px;line-height:1.6;">Diğer tüm şablonların içeriği otomatik olarak bu alana yerleştirilir.</p>',
  },
  Subject: { label: 'E-Posta Konusu', sample: 'Siparişiniz Alındı' },
  FirstName: { label: 'Müşteri / Kullanıcı Adı', sample: 'Ahmet' },
  FullName: { label: 'Ad Soyad', sample: 'Ahmet Yılmaz' },
  CustomerName: { label: 'Müşteri Adı Soyadı', sample: 'Ahmet Yılmaz' },
  CustomerEmail: { label: 'Müşteri E-Posta Adresi', sample: 'ahmet.yilmaz@example.com' },
  CustomerPhone: { label: 'Müşteri Telefon Numarası', sample: '0555 123 45 67' },
  PaymentStatus: { label: 'Ödeme Durumu', sample: 'Ödeme Alındı' },
  AdminOrderUrl: {
    label: 'Yönetim Paneli Sipariş Linki',
    sample: 'https://admin.hurmahane.com/siparisler/1042',
  },
  RecipientName: { label: 'Alıcı Adı Soyadı', sample: 'Zeynep Kaya' },
  SenderName: { label: 'Gönderen Adı Soyadı', sample: 'Ahmet Yılmaz' },
  InviterName: { label: 'Davet Eden Yönetici', sample: 'İbrahim Altan' },
  RoleNames: { label: 'Atanan Roller', sample: 'Operasyon Yöneticisi' },
  NewEmail: { label: 'Yeni E-Posta Adresi', sample: 'ahmet.yeni@hurmahane.com' },
  OrderNumber: { label: 'Sipariş Numarası', sample: 'HR-2026-1042' },
  ReturnNumber: { label: 'İade Talep Numarası', sample: 'IADE-2026-019' },
  SubTotal: { label: 'Ara Toplam', sample: '1.350,00 TL' },
  DiscountTotal: { label: 'İndirim Tutarı', sample: '100,00 TL' },
  ShippingTotal: { label: 'Kargo Ücreti', sample: 'Ücretsiz' },
  TaxTotal: { label: 'KDV Tutarı', sample: '125,00 TL' },
  GrandTotal: { label: 'Genel Toplam', sample: '1.250,00 TL' },
  PaidAmount: { label: 'Ödenen Tutar', sample: '1.250,00 TL' },
  RefundAmount: { label: 'İade Edilen Tutar', sample: '1.250,00 TL' },
  Amount: { label: 'Hediye Kartı Tutarı', sample: '500,00 TL' },
  OldPrice: { label: 'Eski Fiyat', sample: '650,00' },
  NewPrice: { label: 'Yeni İndirimli Fiyat', sample: '490,00' },
  PaymentMethod: { label: 'Ödeme Yöntemi', sample: 'Kredi / Banka Kartı (PayTR)' },
  CarrierName: { label: 'Kargo Firması', sample: 'Kolay Gelsin' },
  ReturnCarrierName: { label: 'İade Kargo Firması', sample: 'Kolay Gelsin' },
  TrackingNumber: { label: 'Kargo Takip Kodu', sample: 'KG849204192' },
  ReturnTrackingCode: { label: 'İade Kargo Kodu', sample: 'KG-IADE-9042' },
  EstimatedDelivery: { label: 'Tahmini Teslim Tarihi', sample: '14.10.2026' },
  DeliveredAt: { label: 'Teslim Edilme Tarihi', sample: '12.10.2026 14:30' },
  ReturnWindowDays: { label: 'İade Süresi (Gün)', sample: '14' },
  RefundDays: { label: 'Banka İade Süresi (İş Günü)', sample: '3-5' },
  CancelReason: { label: 'İptal Nedeni', sample: 'Müşteri talebi üzerine iptal edildi.' },
  ShippingAddress: {
    label: 'Teslimat Adresi',
    sample: 'Ahmet Yılmaz — Atatürk Mah. İstiklal Cad. No: 42 D: 5, Kadıköy / İstanbul',
  },
  Code: { label: 'Doğrulama Kodu (OTP)', sample: '482910' },
  GiftCardCode: { label: 'Hediye Kartı Kodu', sample: 'HEDIYE-2026-500TL' },
  PersonalMessage: {
    label: 'Kişisel Hediye Mesajı',
    sample: 'Yeni yaşın kutlu olsun, ağız tadıyla harcaman dileğiyle!',
  },
  ExpiryDate: { label: 'Son Kullanma Tarihi', sample: '31.12.2027' },
  ExpiryHours: { label: 'Geçerlilik Süresi (Saat)', sample: '24' },
  ExpiryMinutes: { label: 'Geçerlilik Süresi (Dakika)', sample: '10' },
  FailedAttempts: { label: 'Başarısız Giriş Sayısı', sample: '5' },
  LockoutMinutes: { label: 'Kilitlenme Süresi (Dakika)', sample: '15' },
  RequestIp: { label: 'İşlem Yapılan IP Adresi', sample: '88.241.120.45' },
  RequestTime: { label: 'Talep Tarihi ve Saati', sample: '10.10.2026 15:45' },
  ChangeTime: { label: 'Değişiklik Tarihi ve Saati', sample: '10.10.2026 15:45' },
  AttemptTime: { label: 'Deneme Tarihi ve Saati', sample: '10.10.2026 15:45' },
  ProductTitle: { label: 'Ürün Adı', sample: 'Kudüs Medjoul Hurması (Jumbo Boy - 1 Kg)' },
  ConfirmationUrl: {
    label: 'E-Posta Doğrulama Linki',
    sample: 'https://hurmahane.com/eposta-dogrula?token=ornek',
  },
  ConfirmUrl: {
    label: 'Bülten Onay Linki',
    sample: 'https://hurmahane.com/bulten-onay?token=ornek',
  },
  ResetUrl: {
    label: 'Şifre Sıfırlama Linki',
    sample: 'https://hurmahane.com/sifre-sifirla?token=ornek',
  },
  SetupUrl: {
    label: 'Personel Hesap Kurulum Linki',
    sample: 'https://hurmahane.com/personel-kurulum?token=ornek',
  },
  OrderTrackingUrl: {
    label: 'Sipariş Takip Linki',
    sample: 'https://hurmahane.com/siparis-takip/HR-2026-1042',
  },
  TrackingUrl: {
    label: 'Kargo Takip Linki',
    sample: 'https://hurmahane.com/siparis-takip/HR-2026-1042',
  },
  ReviewUrl: {
    label: 'Ürün Değerlendirme Linki',
    sample: 'https://hurmahane.com/hesabim/siparislerim',
  },
  ReturnUrl: {
    label: 'İade Durumu Linki',
    sample: 'https://hurmahane.com/hesabim/iadelerim',
  },
  CartUrl: { label: 'Sepet Linki', sample: 'https://hurmahane.com/sepet' },
  ProductUrl: {
    label: 'Ürün Sayfası Linki',
    sample: 'https://hurmahane.com/urun/kudus-medjoul-hurmasi',
  },
  UnsubscribeUrl: {
    label: 'Bültenden Ayrılma Linki',
    sample: 'https://hurmahane.com/bulten-ayril',
  },
  OrderLinesHtml: {
    label: 'Sipariş Ürünleri Tablosu (HTML)',
    sample: `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;font-size:14px;">
      <thead>
        <tr style="background-color:#f8fafc;border-bottom:1px solid #e2e8f0;color:#475569;">
          <th align="left" style="padding:10px 12px;">Ürün</th>
          <th align="center" style="padding:10px 12px;">Adet</th>
          <th align="right" style="padding:10px 12px;">Tutar</th>
        </tr>
      </thead>
      <tbody>
        <tr style="border-bottom:1px solid #f1f5f9;">
          <td style="padding:12px;color:#0f172a;font-weight:600;">Kudüs Medjoul Hurması (Jumbo - 1 Kg)</td>
          <td align="center" style="padding:12px;color:#475569;">2</td>
          <td align="right" style="padding:12px;color:#0f172a;font-weight:600;">900,00 TL</td>
        </tr>
        <tr>
          <td style="padding:12px;color:#0f172a;font-weight:600;">Acve Peygamber Hurması (Özel Kutu - 500 Gr)</td>
          <td align="center" style="padding:12px;color:#475569;">1</td>
          <td align="right" style="padding:12px;color:#0f172a;font-weight:600;">450,00 TL</td>
        </tr>
      </tbody>
    </table>`,
  },
  CartLinesHtml: {
    label: 'Sepetteki Ürünler Tablosu (HTML)',
    sample: `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;font-size:14px;">
      <tbody>
        <tr style="border-bottom:1px solid #f1f5f9;">
          <td style="padding:12px;color:#0f172a;font-weight:600;">Kudüs Medjoul Hurması (Jumbo - 1 Kg)</td>
          <td align="center" style="padding:12px;color:#475569;">1 Adet</td>
          <td align="right" style="padding:12px;color:#2563eb;font-weight:700;">450,00 TL</td>
        </tr>
      </tbody>
    </table>`,
  },
};

type CategoryFilter = 'all' | 'layout' | 'order' | 'return' | 'auth' | 'marketing';

const CATEGORIES: { key: CategoryFilter; label: string }[] = [
  { key: 'all', label: 'Tümü' },
  { key: 'layout', label: 'Ortak Çerçeve' },
  { key: 'order', label: 'Sipariş & Kargo' },
  { key: 'return', label: 'İade' },
  { key: 'auth', label: 'Üyelik & Güvenlik' },
  { key: 'marketing', label: 'Pazarlama & Diğer' },
];

function getTemplateCategory(key: string): CategoryFilter {
  if (key === 'layout.base') return 'layout';
  if (key.startsWith('order.')) return 'order';
  if (key.startsWith('return.')) return 'return';
  if (key.startsWith('auth.')) return 'auth';
  return 'marketing';
}

function getCategoryBadgeLabel(key: string): string {
  const cat = getTemplateCategory(key);
  switch (cat) {
    case 'layout':
      return 'Ana Çerçeve';
    case 'order':
      return 'Sipariş & Kargo';
    case 'return':
      return 'İade';
    case 'auth':
      return 'Güvenlik';
    default:
      return 'Pazarlama';
  }
}

/** Sunucudan donen goreceli gorsel yolunu tam URL'ye cevirir (e-posta istemcileri icin). */
function toAbsoluteMediaUrl(url: string): string {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '');
  const origin = apiBase || window.location.origin;
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

/** Hem {{Key}} hem {Key} yer tutucularini ornek verilerle degistirir. */
function renderWithPlaceholders(
  template: string,
  values: Record<string, string>,
): string {
  if (!template) return '';
  let out = template.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_, k: string) => {
    const found = Object.keys(values).find((vk) => vk.toLowerCase() === k.toLowerCase());
    return found ? values[found] : `{{${k}}}`;
  });
  out = out.replace(/(?<!\{)\{([A-Za-z][A-Za-z0-9_]*)\}(?!\})/g, (full, k: string) => {
    const found = Object.keys(values).find((vk) => vk.toLowerCase() === k.toLowerCase());
    return found ? values[found] : full;
  });
  return out;
}

export function EmailTemplatesPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, user } = useAuth();
  const canManage = can(P.settings.manage);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Duzenleme formu
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  // Degisken ekleme bicimi: {Degisken} veya {{Degisken}}
  const [braceStyle, setBraceStyle] = useState<'single' | 'double'>('single');

  // Editor gorunum modu: 'visual' (Gorsel WYSIWYG), 'code' (HTML Kod), 'split' (Editor + Onizleme)
  const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');
  const [showLivePreview, setShowLivePreview] = useState(true);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [useSampleData, setUseSampleData] = useState(true);

  // Son odaklanilan alan: Konu mu yoksa govde mi?
  const lastFocusedTargetRef = useRef<'subject' | 'body'>('body');
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const codeTextareaRef = useRef<HTMLTextAreaElement>(null);
  const visualEditorRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);

  // Gorsel editor icinde tiklanan <img> elementi (hizli boyutlandirma icin)
  const [selectedImgEl, setSelectedImgEl] = useState<HTMLImageElement | null>(null);

  // Modallar
  const [showImageModal, setShowImageModal] = useState(false);
  const [imgUrl, setImgUrl] = useState('');
  const [imgAlt, setImgAlt] = useState('');
  const [imgWidth, setImgWidth] = useState('100%');
  const [imgAlign, setImgAlign] = useState<'left' | 'center' | 'right'>('center');
  const [imgRounded, setImgRounded] = useState(true);
  const [imgLinkUrl, setImgLinkUrl] = useState('');
  const [isUploadingImg, setIsUploadingImg] = useState(false);
  const [isDragOverImg, setIsDragOverImg] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Buton (CTA) Ekleme Modali
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [btnText, setBtnText] = useState('SİPARİŞİMİ GÖRÜNTÜLE');
  const [btnUrl, setBtnUrl] = useState('{OrderTrackingUrl}');
  const [btnBgColor, setBtnBgColor] = useState('#2563eb');
  const [btnTextColor, setBtnTextColor] = useState('#ffffff');
  const [btnAlign, setBtnAlign] = useState<'left' | 'center' | 'right'>('center');

  // Bilgi/Uyari Kutusu Modali
  const [showCalloutModal, setShowCalloutModal] = useState(false);
  const [calloutTone, setCalloutTone] = useState<'info' | 'success' | 'warning' | 'danger' | 'coupon'>('info');
  const [calloutTitle, setCalloutTitle] = useState('Önemli Bilgilendirme');
  const [calloutBody, setCalloutBody] = useState('Buraya müşterinize iletmek istediğiniz önemli detayı yazabilirsiniz.');

  // Test E-postasi Gonderme Modali
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email ?? '');

  // Yeni Ozel Sablon Olusturma Modali
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newSubject, setNewSubject] = useState('{StoreName} — Bilgilendirme');

  // Sifirlama ve Silme Onaylari
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Sablonlari cek
  const templatesQuery = useQuery({
    queryKey: ['admin', 'email-templates'],
    queryFn: () => adminSettingsApi.emailTemplates(),
  });

  // Magaza kimlik ayarlarini cek (canli onizlemede Ayarlar -> Magaza Kimlik Bilgileri gorunsun)
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminSettingsApi.getAll(),
  });

  const templates = templatesQuery.data ?? [];

  const layoutTemplate = useMemo(
    () => templates.find((t) => t.key === 'layout.base') ?? null,
    [templates],
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const isLayoutSelected = selectedTemplate?.key === 'layout.base';

  // Ilk yuklemede siparis onayini veya ilk sablonu sec
  useEffect(() => {
    if (templates.length > 0 && selectedId === null) {
      const orderConf = templates.find((t) => t.key === 'order.confirmation');
      const initial = orderConf ?? templates[0];
      selectTemplate(initial);
    }
  }, [templates, selectedId]);

  const selectTemplate = useCallback((tpl: EmailTemplateDto) => {
    setSelectedId(tpl.id);
    setName(tpl.name);
    setSubject(tpl.subject);
    setHtmlBody(tpl.htmlBody);
    setIsActive(tpl.isActive);
    setIsDirty(false);
    setSelectedImgEl(null);

    // layout.base tam HTML belgesi oldugu icin kod gorunumuyle baslamak daha guvenlidir,
    // ancak kullanici istedigi an gorsel moda da gecebilir.
    if (tpl.key === 'layout.base') {
      setEditorMode('code');
    } else {
      setEditorMode('visual');
    }
  }, []);

  // Gorsel editor icerigini htmlBody ile senkron tut
  useEffect(() => {
    if (editorMode === 'visual' && visualEditorRef.current) {
      if (visualEditorRef.current.innerHTML !== htmlBody) {
        visualEditorRef.current.innerHTML = htmlBody;
      }
    }
  }, [selectedId, editorMode]);

  // Filtrelenmis sablon listesi
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      if (category !== 'all' && getTemplateCategory(t.key) !== category) {
        return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        return (
          t.name.toLowerCase().includes(q) ||
          t.key.toLowerCase().includes(q) ||
          t.subject.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [templates, category, search]);

  // Secili sablon icin kullanilabilir degiskenler
  const availablePlaceholders = useMemo(() => {
    const base = new Set<string>([
      ...(selectedTemplate?.availablePlaceholders ?? []),
      'StoreName',
      'StorefrontUrl',
      'SupportEmail',
      'SupportPhone',
      'CompanyAddress',
      'Year',
    ]);
    return Array.from(base);
  }, [selectedTemplate]);

  // Ornek veri haritasi (Ayarlar -> Magaza Kimlik Bilgileri ile birlestirilmis)
  const sampleValues = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(PLACEHOLDER_DICTIONARY).forEach(([k, v]) => {
      map[k] = v.sample;
    });

    if (settingsQuery.data) {
      const cfg = Object.fromEntries(
        settingsQuery.data.map((s) => [s.key, (s.value ?? '').trim()]),
      );
      const storeName = cfg['store.name'] || cfg['smtp.from_display_name'];
      const supportEmail = cfg['store.email'] || cfg['smtp.from_address'];
      const supportPhone = cfg['store.phone'];
      const companyAddress = cfg['store.address'];
      const logoUrl = cfg['store.logo_url'];

      if (storeName) map.StoreName = storeName;
      if (supportEmail) map.SupportEmail = supportEmail;
      if (supportPhone) map.SupportPhone = supportPhone;
      if (companyAddress !== undefined && companyAddress !== '') {
        map.CompanyAddress = companyAddress;
      }
      if (logoUrl) map.LogoUrl = logoUrl;
    }

    return map;
  }, [settingsQuery.data]);

  // Canli onizleme HTML'i (layout.base ile birlestirilmis)
  const previewHtml = useMemo(() => {
    const activeLayoutHtml =
      isLayoutSelected ? htmlBody : layoutTemplate?.htmlBody ?? '{{Content}}';

    if (!useSampleData) {
      if (isLayoutSelected) {
        return activeLayoutHtml;
      }
      return activeLayoutHtml
        .replace(/\{\{\s*Content\s*\}\}/gi, htmlBody)
        .replace(/\{Content\}/gi, htmlBody);
    }

    const renderedSubject = renderWithPlaceholders(subject, sampleValues);
    if (isLayoutSelected) {
      return renderWithPlaceholders(activeLayoutHtml, {
        ...sampleValues,
        Subject: renderedSubject,
      });
    }

    const renderedBody = renderWithPlaceholders(htmlBody, sampleValues);
    return renderWithPlaceholders(activeLayoutHtml, {
      ...sampleValues,
      Subject: renderedSubject,
      Content: renderedBody,
    });
  }, [htmlBody, subject, isLayoutSelected, layoutTemplate, useSampleData, sampleValues]);

  const previewSubject = useMemo(() => {
    return useSampleData ? renderWithPlaceholders(subject, sampleValues) : subject;
  }, [subject, useSampleData, sampleValues]);

  // Secimi kaydet / geri yukle (Gorsel Editor)
  const saveVisualSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && visualEditorRef.current?.contains(sel.anchorNode)) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreVisualSelection = () => {
    if (savedSelectionRef.current && window.getSelection) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
      }
    }
  };

  const syncFromVisualEditor = () => {
    if (!visualEditorRef.current) return;
    const currentHtml = visualEditorRef.current.innerHTML;
    setHtmlBody(currentHtml);
    setIsDirty(true);
  };

  const execVisualCommand = (cmd: string, value?: string) => {
    if (editorMode !== 'visual' || !visualEditorRef.current) return;
    visualEditorRef.current.focus();
    restoreVisualSelection();
    document.execCommand(cmd, false, value);
    syncFromVisualEditor();
    saveVisualSelection();
  };

  /** Herhangi bir HTML parcasini aktif editore (Gorsel veya Kod) ekler. */
  const insertSnippetIntoBody = (snippet: string) => {
    if (editorMode === 'visual' && visualEditorRef.current) {
      visualEditorRef.current.focus();
      restoreVisualSelection();
      const inserted = document.execCommand('insertHTML', false, snippet);
      if (!inserted) {
        visualEditorRef.current.innerHTML += snippet;
      }
      syncFromVisualEditor();
      saveVisualSelection();
    } else if (codeTextareaRef.current) {
      const el = codeTextareaRef.current;
      const start = el.selectionStart ?? htmlBody.length;
      const end = el.selectionEnd ?? htmlBody.length;
      const next = htmlBody.slice(0, start) + snippet + htmlBody.slice(end);
      setHtmlBody(next);
      setIsDirty(true);
      setTimeout(() => {
        el.focus();
        const pos = start + snippet.length;
        el.setSelectionRange(pos, pos);
      }, 0);
    } else {
      setHtmlBody((prev) => prev + '\n' + snippet);
      setIsDirty(true);
    }
  };

  /** Degisken cipine tiklandiginda Konu veya Govdeye ekler. */
  const handleInsertPlaceholder = (key: string) => {
    const token = braceStyle === 'single' ? `{${key}}` : `{{${key}}}`;

    if (lastFocusedTargetRef.current === 'subject' && subjectInputRef.current) {
      const el = subjectInputRef.current;
      const start = el.selectionStart ?? subject.length;
      const end = el.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
      setIsDirty(true);
      setTimeout(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      }, 0);
      toast.success(`${token} konu başlığına eklendi.`);
      return;
    }

    insertSnippetIntoBody(token);
    toast.success(`${token} şablon içeriğine eklendi.`);
  };

  /* -------------------- Gorsel Yukleme ve Ekleme -------------------- */

  const handleImageFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görsel dosyası seçin (PNG, JPG, WebP, GIF).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Görsel boyutu en fazla 10 MB olabilir.');
      return;
    }

    setIsUploadingImg(true);
    try {
      const res = await adminFilesApi.uploadImage(file, 'email-templates');
      if (res?.url) {
        const fullUrl = toAbsoluteMediaUrl(res.url);
        setImgUrl(fullUrl);
        if (!imgAlt) {
          setImgAlt(file.name.replace(/\.[^.]+$/, ''));
        }
        toast.success('Görsel sunucuya yüklendi.');
      } else {
        toast.error('Görsel URL bilgisi alınamadı.');
      }
    } catch (err) {
      toast.fromError(err, 'Görsel yüklenirken hata oluştu.');
    } finally {
      setIsUploadingImg(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOverImg(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleImageFileUpload(file);
  };

  const handleInsertImageConfirm = () => {
    if (!imgUrl.trim()) {
      toast.error('Lütfen bir görsel yükleyin veya görsel URL adresi girin.');
      return;
    }

    const cleanUrl = toAbsoluteMediaUrl(imgUrl.trim());
    const radiusStyle = imgRounded ? 'border-radius:10px;' : '';
    const widthStyle =
      imgWidth === '100%'
        ? 'width:100%;max-width:100%;'
        : `width:${imgWidth};max-width:100%;`;

    const imgTag = `<img src="${cleanUrl}" alt="${imgAlt.trim() || 'Görsel'}" style="display:inline-block;${widthStyle}height:auto;${radiusStyle}border:0;" />`;
    const wrappedImg = imgLinkUrl.trim()
      ? `<a href="${imgLinkUrl.trim()}" target="_blank" style="text-decoration:none;">${imgTag}</a>`
      : imgTag;

    const htmlBlock = `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin:18px 0;"><tr><td align="${imgAlign}">${wrappedImg}</td></tr></table>`;

    insertSnippetIntoBody(htmlBlock);
    setShowImageModal(false);
    setImgUrl('');
    setImgAlt('');
    setImgLinkUrl('');
    toast.success('Görsel şablona eklendi.');
  };

  /* -------------------- Secili Gorsel HizliDuzenleme -------------------- */

  const handleResizeSelectedImg = (newWidth: string) => {
    if (!selectedImgEl) return;
    selectedImgEl.style.width = newWidth;
    selectedImgEl.style.maxWidth = '100%';
    selectedImgEl.style.height = 'auto';
    syncFromVisualEditor();
  };

  const handleAlignSelectedImg = (align: 'left' | 'center' | 'right') => {
    if (!selectedImgEl) return;
    const td = selectedImgEl.closest('td');
    if (td) {
      td.setAttribute('align', align);
    } else {
      selectedImgEl.style.display = 'block';
      selectedImgEl.style.marginLeft = align === 'center' || align === 'right' ? 'auto' : '0';
      selectedImgEl.style.marginRight = align === 'center' || align === 'left' ? 'auto' : '0';
    }
    syncFromVisualEditor();
  };

  const handleDeleteSelectedImg = () => {
    if (!selectedImgEl) return;
    const wrapperTable = selectedImgEl.closest('table');
    if (wrapperTable && wrapperTable.querySelectorAll('img').length === 1 && wrapperTable.innerText.trim() === '') {
      wrapperTable.remove();
    } else {
      selectedImgEl.remove();
    }
    setSelectedImgEl(null);
    syncFromVisualEditor();
  };

  /* -------------------- Hazir Blok Ekleme (Buton, Kutu, Cizgi) -------------------- */

  const handleInsertButtonConfirm = () => {
    if (!btnText.trim()) return;
    const href = btnUrl.trim() || '#';
    const html = `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin:24px 0;">
  <tr>
    <td align="${btnAlign}">
      <a href="${href}" target="_blank" style="display:inline-block;background-color:${btnBgColor};color:${btnTextColor} !important;font-size:15px;font-weight:600;text-decoration:none;padding:14px 34px;border-radius:10px;box-shadow:0 4px 12px rgba(0,0,0,0.12);">${btnText.trim()}</a>
    </td>
  </tr>
</table>`;
    insertSnippetIntoBody(html);
    setShowButtonModal(false);
    toast.success('Buton şablona eklendi.');
  };

  const handleInsertCalloutConfirm = () => {
    let html = '';
    if (calloutTone === 'coupon') {
      html = `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin:22px 0;">
  <tr>
    <td align="center">
      <div style="display:inline-block;background-color:#f8fafc;border:2px dashed #94a3b8;border-radius:12px;padding:16px 32px;text-align:center;">
        <div style="font-size:12px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">${calloutTitle}</div>
        <span style="font-family:monospace;font-size:26px;font-weight:800;letter-spacing:5px;color:#0f172a;">${calloutBody}</span>
      </div>
    </td>
  </tr>
</table>`;
    } else {
      const styles: Record<string, { bg: string; border: string; text: string }> = {
        info: { bg: '#eff6ff', border: '#3b82f6', text: '#1e3a8a' },
        success: { bg: '#f0fdf4', border: '#22c55e', text: '#14532d' },
        warning: { bg: '#fffbeb', border: '#f59e0b', text: '#78350f' },
        danger: { bg: '#fef2f2', border: '#ef4444', text: '#7f1d1d' },
      };
      const st = styles[calloutTone] ?? styles.info;
      html = `<div style="background-color:${st.bg};border-left:4px solid ${st.border};border-radius:0 8px 8px 0;padding:14px 16px;margin:20px 0;font-size:14px;color:${st.text};line-height:1.5;">
  ${calloutTitle.trim() ? `<strong style="display:block;margin-bottom:4px;">${calloutTitle.trim()}</strong>` : ''}
  <span>${calloutBody}</span>
</div>`;
    }
    insertSnippetIntoBody(html);
    setShowCalloutModal(false);
    toast.success('Bilgi kutusu şablona eklendi.');
  };

  const handleInsertDivider = () => {
    insertSnippetIntoBody(
      `<hr style="border:0;border-top:1px solid #e2e8f0;margin:24px 0;" />`,
    );
  };

  const handleInsertTwoColumnTable = () => {
    const html = `<div style="background-color:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:16px;margin:20px 0;">
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="font-size:14px;">
    <tr>
      <td style="padding:6px 0;color:#64748b;">Sipariş Numarası:</td>
      <td align="right" style="padding:6px 0;font-weight:700;color:#0f172a;">{OrderNumber}</td>
    </tr>
    <tr>
      <td style="padding:6px 0;color:#64748b;">Genel Toplam:</td>
      <td align="right" style="padding:6px 0;font-weight:700;color:#2563eb;">{GrandTotal}</td>
    </tr>
  </table>
</div>`;
    insertSnippetIntoBody(html);
    toast.success('2 sütunlu bilgi tablosu eklendi.');
  };

  /* -------------------- API Mutations -------------------- */

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error('Şablon seçilmedi.');
      const dto: EmailTemplateUpsertDto = {
        name: name.trim(),
        subject: subject.trim(),
        htmlBody,
        isActive: isLayoutSelected ? true : isActive,
        availablePlaceholders,
      };
      return adminSettingsApi.updateEmailTemplate(selectedTemplate.id, dto);
    },
    onSuccess: (updated) => {
      toast.success('E-posta şablonu başarıyla kaydedildi.');
      setIsDirty(false);
      queryClient.setQueryData<EmailTemplateDto[]>(['admin', 'email-templates'], (old) =>
        old ? old.map((item) => (item.id === updated.id ? updated : item)) : [updated],
      );
    },
    onError: (err) => toast.fromError(err, 'Şablon kaydedilirken hata oluştu.'),
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error('Şablon seçilmedi.');
      return adminSettingsApi.resetEmailTemplate(selectedTemplate.id);
    },
    onSuccess: (resetTpl) => {
      setConfirmReset(false);
      selectTemplate(resetTpl);
      if (visualEditorRef.current) {
        visualEditorRef.current.innerHTML = resetTpl.htmlBody;
      }
      toast.success('Şablon fabrika varsayılan tasarımına sıfırlandı.');
      queryClient.setQueryData<EmailTemplateDto[]>(['admin', 'email-templates'], (old) =>
        old ? old.map((item) => (item.id === resetTpl.id ? resetTpl : item)) : [resetTpl],
      );
    },
    onError: (err) => toast.fromError(err, 'Şablon sıfırlanırken hata oluştu.'),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const dto: EmailTemplateUpsertDto = {
        key: newKey.trim() || newName.trim(),
        name: newName.trim(),
        subject: newSubject.trim(),
        htmlBody: `<h1 style="margin:0 0 16px 0;font-size:22px;font-weight:700;color:#0f172a;">Merhaba {CustomerName} 👋</h1>\n<p style="margin:0 0 16px 0;font-size:15px;color:#475569;line-height:1.6;"><strong>{StoreName}</strong> mağazamızdan size özel bilgilendirme mesajıdır.</p>`,
        isActive: true,
        availablePlaceholders: ['CustomerName', 'StoreName', 'StorefrontUrl', 'SupportEmail'],
      };
      return adminSettingsApi.createEmailTemplate(dto);
    },
    onSuccess: (created) => {
      setShowCreateModal(false);
      setNewKey('');
      setNewName('');
      toast.success('Yeni e-posta şablonu oluşturuldu.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] });
      selectTemplate(created);
    },
    onError: (err) => toast.fromError(err, 'Şablon oluşturulamadı.'),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error('Şablon seçilmedi.');
      return adminSettingsApi.deleteEmailTemplate(selectedTemplate.id);
    },
    onSuccess: () => {
      setConfirmDelete(false);
      toast.success('Özel şablon silindi.');
      setSelectedId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] });
    },
    onError: (err) => toast.fromError(err, 'Şablon silinemedi.'),
  });

  const testEmailMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error('Şablon seçilmedi.');
      return adminSettingsApi.sendTemplateTestEmail(selectedTemplate.id, {
        toEmail: testEmail.trim(),
        subject,
        htmlBody,
      });
    },
    onSuccess: (msg) => {
      setShowTestModal(false);
      toast.success(msg ?? `Test e-postası ${testEmail} adresine gönderildi.`);
    },
    onError: (err) => toast.fromError(err, 'Test e-postası gönderilemedi.'),
  });

  if (templatesQuery.isLoading) return <PageLoader />;
  if (templatesQuery.isError) {
    return (
      <ErrorState
        error={templatesQuery.error}
        onRetry={() => {
          void templatesQuery.refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="E-Posta Şablonları & Tasarım Stüdyosu"
        description="Müşterilerinize giden tüm otomatik e-postaları görsel editörle veya HTML kodlarıyla düzenleyin, resim/logo ekleyin ve canlı önizleyin."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canManage && (
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-medium text-ink-700 hover:bg-ink-50 transition"
              >
                <Plus className="h-4 w-4" />
                Yeni Şablon
              </button>
            )}

            {selectedTemplate && canManage && (
              <button
                type="button"
                onClick={() => {
                  if (!testEmail && user?.email) setTestEmail(user.email);
                  setShowTestModal(true);
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50/80 px-3.5 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition"
              >
                <Send className="h-3.5 w-3.5" />
                Bu Şablonu Test Gönder
              </button>
            )}

            {selectedTemplate && selectedTemplate.isSystem && canManage && (
              <button
                type="button"
                onClick={() => setConfirmReset(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs font-medium text-amber-800 hover:bg-amber-100 transition"
                title="Fabrika ayarlarındaki orijinal HTML tasarımına geri döndür"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Varsayılana Sıfırla
              </button>
            )}

            {selectedTemplate && !selectedTemplate.isSystem && canManage && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-100 transition"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Şablonu Sil
              </button>
            )}

            {selectedTemplate && canManage && (
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
                className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-ink-800 disabled:opacity-50 transition"
              >
                {saveMutation.isPending ? (
                  <Spinner className="h-3.5 w-3.5 text-white" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Değişiklikleri Kaydet
                {isDirty && (
                  <span className="ml-0.5 h-2 w-2 rounded-full bg-amber-400" title="Kaydedilmemiş değişiklik var" />
                )}
              </button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        {/* ================= SOL PANEL: SABLON LISTESI ================= */}
        <div className="xl:col-span-3 flex flex-col rounded-xl border border-ink-200 bg-white shadow-xs overflow-hidden">
          <div className="border-b border-ink-100 p-3 space-y-2.5 bg-ink-50/50">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Şablon ara (Sipariş, Kargo...)"
                className="w-full rounded-lg border border-ink-200 bg-white py-1.5 pl-9 pr-3 text-xs text-ink-900 placeholder:text-ink-400 focus:border-ink-900 focus:outline-none"
              />
            </div>

            <div className="flex flex-wrap gap-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setCategory(cat.key)}
                  className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                    category === cat.key
                      ? 'bg-ink-900 text-white'
                      : 'bg-white border border-ink-200 text-ink-600 hover:bg-ink-100'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="divide-y divide-ink-100 max-h-[750px] overflow-y-auto">
            {filteredTemplates.map((tpl) => {
              const active = tpl.id === selectedId;
              const isMaster = tpl.key === 'layout.base';
              return (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => selectTemplate(tpl)}
                  className={`w-full text-left px-3.5 py-3 transition flex flex-col gap-1 ${
                    active
                      ? 'bg-blue-50/80 border-l-4 border-l-blue-600'
                      : 'hover:bg-ink-50/70'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-xs text-ink-900 truncate flex items-center gap-1.5">
                      {isMaster ? (
                        <LayoutTemplate className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                      ) : (
                        <Mail className="h-3.5 w-3.5 text-ink-400 shrink-0" />
                      )}
                      {tpl.name}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {tpl.isCustomized && (
                        <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800">
                          Özel
                        </span>
                      )}
                      {!tpl.isActive && (
                        <span className="rounded bg-ink-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-600">
                          Pasif
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-ink-500">
                    <span className="font-mono text-[10px] text-ink-400 truncate">{tpl.key}</span>
                    <span>{getCategoryBadgeLabel(tpl.key)}</span>
                  </div>
                </button>
              );
            })}

            {filteredTemplates.length === 0 && (
              <div className="p-6 text-center text-xs text-ink-500">
                Aramanıza uygun e-posta şablonu bulunamadı.
              </div>
            )}
          </div>
        </div>

        {/* ================= SAG PANEL: EDITOR & CANLI ONIZLEME ================= */}
        <div className="xl:col-span-9 space-y-4">
          {selectedTemplate ? (
            <>
              {/* Ust Bilgi & Konu Basligi Karti */}
              <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-xs space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 pb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold text-ink-900">{selectedTemplate.name}</h2>
                      <span className="rounded-md bg-ink-100 px-2 py-0.5 font-mono text-xs text-ink-600">
                        {selectedTemplate.key}
                      </span>
                      {selectedTemplate.isCustomized ? (
                        <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                          Özelleştirildi
                        </span>
                      ) : (
                        <span className="rounded-full bg-ink-100 px-2.5 py-0.5 text-xs font-medium text-ink-600">
                          Varsayılan Tasarım
                        </span>
                      )}
                    </div>
                    {selectedTemplate.updatedAt && (
                      <p className="mt-0.5 text-[11px] text-ink-500">
                        Son düzenleme: {formatDateTime(selectedTemplate.updatedAt)}
                      </p>
                    )}
                  </div>

                  {!isLayoutSelected && (
                    <label className="flex items-center gap-2 cursor-pointer select-none rounded-lg border border-ink-200 px-3 py-1.5 bg-ink-50/50">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => {
                          setIsActive(e.target.checked);
                          setIsDirty(true);
                        }}
                        className="h-4 w-4 rounded border-ink-300 text-ink-900"
                      />
                      <span className="text-xs font-medium text-ink-800">
                        Otomatik Gönderim Aktif
                      </span>
                    </label>
                  )}
                </div>

                {isLayoutSelected && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900 flex items-start gap-2.5">
                    <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <strong>Ortak E-Posta Düzeni (Master Layout):</strong> Bu şablon tüm e-postaların dış çerçevesidir (Üst Logo/Header ve Alt Bilgi/Footer). İçerideki <code>{'{{Content}}'}</code> veya <code>{'{Content}'}</code> alanına diğer şablonların gövdesi otomatik yerleştirilir.
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Field label="Şablon Adı (Panel İçin)">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setIsDirty(true);
                      }}
                      className="input"
                    />
                  </Field>

                  <div className="md:col-span-2">
                    <Field
                      label="E-Posta Konu Başlığı (Subject)"
                      hint="Değişken ekleyebilirsiniz. Örn: {StoreName} — Siparişiniz alındı ({OrderNumber})"
                    >
                      <input
                        ref={subjectInputRef}
                        type="text"
                        value={subject}
                        onFocus={() => {
                          lastFocusedTargetRef.current = 'subject';
                        }}
                        onChange={(e) => {
                          setSubject(e.target.value);
                          setIsDirty(true);
                        }}
                        className="input font-medium"
                        placeholder="E-posta konusu..."
                      />
                    </Field>
                  </div>
                </div>

                {/* Tiklanabilir Dinamik Degiskenler Cubugu */}
                <div className="rounded-lg border border-ink-200 bg-ink-50/60 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-800">
                      <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                      <span>Tıklanabilir Dinamik Değişkenler</span>
                      <span className="font-normal text-ink-500">
                        (Tıkladığınızda imlecin olduğu yere otomatik eklenir — hem <code>{'{Değişken}'}</code> hem <code>{'{{Değişken}}'}</code> desteklenir)
                      </span>
                    </div>

                    <div className="flex items-center gap-1 rounded-md border border-ink-200 bg-white p-0.5 text-[11px]">
                      <button
                        type="button"
                        onClick={() => setBraceStyle('single')}
                        className={`rounded px-2 py-0.5 font-mono transition ${
                          braceStyle === 'single'
                            ? 'bg-ink-900 text-white font-semibold'
                            : 'text-ink-600 hover:bg-ink-100'
                        }`}
                      >
                        {'{Değişken}'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setBraceStyle('double')}
                        className={`rounded px-2 py-0.5 font-mono transition ${
                          braceStyle === 'double'
                            ? 'bg-ink-900 text-white font-semibold'
                            : 'text-ink-600 hover:bg-ink-100'
                        }`}
                      >
                        {'{{Değişken}}'}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {availablePlaceholders.map((ph) => {
                      const meta = PLACEHOLDER_DICTIONARY[ph];
                      const displayToken = braceStyle === 'single' ? `{${ph}}` : `{{${ph}}}`;
                      return (
                        <button
                          key={ph}
                          type="button"
                          onMouseDown={(e) => {
                            // Odagin kaybolmasini onle
                            e.preventDefault();
                            handleInsertPlaceholder(ph);
                          }}
                          title={
                            meta
                              ? `${meta.label} — Örnek: ${meta.sample.replace(/<[^>]+>/g, '')}`
                              : ph
                          }
                          className="group inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-2.5 py-1 text-xs transition hover:border-blue-400 hover:bg-blue-50/70"
                        >
                          <span className="font-mono font-semibold text-blue-700">
                            {displayToken}
                          </span>
                          {meta && (
                            <span className="text-[11px] text-ink-500 group-hover:text-ink-800">
                              {meta.label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Editor + Canli OnizlemeCalisma Alani */}
              <div
                className={`grid grid-cols-1 gap-4 ${
                  showLivePreview ? 'lg:grid-cols-12' : 'lg:grid-cols-1'
                }`}
              >
                {/* SOL: GORSEL EDITOR VEYA HTML KOD EDITORU */}
                <div
                  className={`${
                    showLivePreview ? 'lg:col-span-6' : 'lg:col-span-12'
                  } flex flex-col rounded-xl border border-ink-200 bg-white shadow-xs overflow-hidden`}
                >
                  {/* Mod Secici ve HizliBilesen Ekleme Cubugu */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-200 bg-ink-50 px-3 py-2">
                    <div className="flex items-center gap-1 rounded-lg border border-ink-200 bg-white p-0.5">
                      <button
                        type="button"
                        onClick={() => setEditorMode('visual')}
                        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                          editorMode === 'visual'
                            ? 'bg-ink-900 text-white'
                            : 'text-ink-600 hover:bg-ink-100'
                        }`}
                      >
                        <Wand2 className="h-3.5 w-3.5" />
                        Görsel Editör
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (editorMode === 'visual' && visualEditorRef.current) {
                            setHtmlBody(visualEditorRef.current.innerHTML);
                          }
                          setEditorMode('code');
                        }}
                        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                          editorMode === 'code'
                            ? 'bg-ink-900 text-white'
                            : 'text-ink-600 hover:bg-ink-100'
                        }`}
                      >
                        <Code2 className="h-3.5 w-3.5" />
                        HTML Kodu
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowLivePreview((v) => !v)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition ${
                        showLivePreview
                          ? 'border-blue-200 bg-blue-50 text-blue-700'
                          : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-100'
                      }`}
                    >
                      <Columns className="h-3.5 w-3.5" />
                      {showLivePreview ? 'Yan Yana Önizleme Açık' : 'Canlı Önizlemeyi Aç'}
                    </button>
                  </div>

                  {/* Hizli Blok Ekleme Cubugu (Resim, Buton, Kutu, Tablo) */}
                  <div className="flex flex-wrap items-center gap-1.5 border-b border-ink-100 bg-white px-3 py-2">
                    <span className="text-[11px] font-semibold text-ink-500 mr-1">
                      Hızlı Ekle:
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        saveVisualSelection();
                        setShowImageModal(true);
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50/80 px-2.5 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 transition"
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                      Resim / Logo Yükle
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        saveVisualSelection();
                        setShowButtonModal(true);
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50/80 px-2.5 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 transition"
                    >
                      <SquareMousePointer className="h-3.5 w-3.5" />
                      Buton Ekle
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        saveVisualSelection();
                        setShowCalloutModal(true);
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50/80 px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition"
                    >
                      <Info className="h-3.5 w-3.5" />
                      Bilgi / Kupon Kutusu
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        saveVisualSelection();
                        handleInsertTwoColumnTable();
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-ink-50 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 transition"
                    >
                      <Table className="h-3.5 w-3.5" />
                      Bilgi Tablosu
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        saveVisualSelection();
                        handleInsertDivider();
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-ink-50 px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 transition"
                    >
                      <Minus className="h-3.5 w-3.5" />
                      Ayırıcı Çizgi
                    </button>
                  </div>

                  {/* Gorsel Editor Bicimlendirme Cubugu (Sadece Gorsel Modda) */}
                  {editorMode === 'visual' && (
                    <div className="flex flex-wrap items-center gap-1 border-b border-ink-100 bg-ink-50/40 px-3 py-1.5 select-none">
                      <button
                        type="button"
                        onClick={() => execVisualCommand('formatBlock', '<p>')}
                        className="rounded px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-200/60"
                        title="Normal Paragraf"
                      >
                        <Type className="h-3.5 w-3.5 inline mr-0.5" />
                        Metin
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('formatBlock', '<h1>')}
                        className="rounded px-2 py-1 text-xs font-bold text-ink-700 hover:bg-ink-200/60"
                        title="Başlık 1"
                      >
                        <Heading1 className="h-3.5 w-3.5 inline" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('formatBlock', '<h2>')}
                        className="rounded px-2 py-1 text-xs font-bold text-ink-700 hover:bg-ink-200/60"
                        title="Başlık 2"
                      >
                        <Heading2 className="h-3.5 w-3.5 inline" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('formatBlock', '<h3>')}
                        className="rounded px-2 py-1 text-xs font-bold text-ink-700 hover:bg-ink-200/60"
                        title="Başlık 3"
                      >
                        <Heading3 className="h-3.5 w-3.5 inline" />
                      </button>

                      <div className="mx-1 h-4 w-px bg-ink-200" />

                      <button
                        type="button"
                        onClick={() => execVisualCommand('bold')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Kalın (Ctrl+B)"
                      >
                        <Bold className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('italic')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="İtalik (Ctrl+I)"
                      >
                        <Italic className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('underline')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Altı Çizili (Ctrl+U)"
                      >
                        <Underline className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('strikeThrough')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Üstü Çizili"
                      >
                        <Strikethrough className="h-3.5 w-3.5" />
                      </button>

                      <div className="mx-1 h-4 w-px bg-ink-200" />

                      {/* Yazi Rengi */}
                      <label
                        className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-ink-700 hover:bg-ink-200/60 cursor-pointer"
                        title="Yazı Rengi Seç"
                      >
                        <Palette className="h-3.5 w-3.5 text-blue-600" />
                        <span className="text-[11px]">Renk</span>
                        <input
                          type="color"
                          defaultValue="#0f172a"
                          onChange={(e) => execVisualCommand('foreColor', e.target.value)}
                          className="h-4 w-4 cursor-pointer border-0 bg-transparent p-0"
                        />
                      </label>

                      <div className="mx-1 h-4 w-px bg-ink-200" />

                      <button
                        type="button"
                        onClick={() => execVisualCommand('justifyLeft')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Sola Hizala"
                      >
                        <AlignLeft className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('justifyCenter')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Ortala"
                      >
                        <AlignCenter className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('justifyRight')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Sağa Hizala"
                      >
                        <AlignRight className="h-3.5 w-3.5" />
                      </button>

                      <div className="mx-1 h-4 w-px bg-ink-200" />

                      <button
                        type="button"
                        onClick={() => execVisualCommand('insertUnorderedList')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Madde İşaretli Liste"
                      >
                        <List className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execVisualCommand('insertOrderedList')}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Numaralı Liste"
                      >
                        <ListOrdered className="h-3.5 w-3.5" />
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const url = window.prompt(
                            'Bağlantı (Link) URL adresini veya değişkeni girin:',
                            'https://',
                          );
                          if (url) execVisualCommand('createLink', url);
                        }}
                        className="rounded p-1.5 text-ink-700 hover:bg-ink-200/60"
                        title="Bağlantı (Link) Ekle"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Secili Gorsel (Image) Hizli Boyutlandirma Cubugu */}
                  {editorMode === 'visual' && selectedImgEl && (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-200 bg-blue-50 px-3 py-2 text-xs">
                      <span className="font-semibold text-blue-900 flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5 text-blue-600" />
                        Seçili Görsel Ayarları:
                      </span>
                      <div className="flex flex-wrap items-center gap-1">
                        {(['120px', '180px', '280px', '50%', '100%'] as const).map((w) => (
                          <button
                            key={w}
                            type="button"
                            onClick={() => handleResizeSelectedImg(w)}
                            className="rounded border border-blue-200 bg-white px-2 py-0.5 text-[11px] font-medium text-blue-800 hover:bg-blue-100"
                          >
                            {w}
                          </button>
                        ))}
                        <div className="mx-1 h-3.5 w-px bg-blue-200" />
                        <button
                          type="button"
                          onClick={() => handleAlignSelectedImg('left')}
                          className="rounded border border-blue-200 bg-white px-2 py-0.5 text-[11px] text-blue-800 hover:bg-blue-100"
                        >
                          Sol
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAlignSelectedImg('center')}
                          className="rounded border border-blue-200 bg-white px-2 py-0.5 text-[11px] text-blue-800 hover:bg-blue-100"
                        >
                          Orta
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAlignSelectedImg('right')}
                          className="rounded border border-blue-200 bg-white px-2 py-0.5 text-[11px] text-blue-800 hover:bg-blue-100"
                        >
                          Sağ
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteSelectedImg}
                          className="ml-1 rounded border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
                        >
                          Görseli Kaldır
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Editor Govdesi */}
                  {editorMode === 'visual' ? (
                    <div
                      ref={visualEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      onFocus={() => {
                        lastFocusedTargetRef.current = 'body';
                      }}
                      onInput={syncFromVisualEditor}
                      onKeyUp={saveVisualSelection}
                      onMouseUp={(e) => {
                        saveVisualSelection();
                        const target = e.target as HTMLElement;
                        if (target.tagName === 'IMG') {
                          setSelectedImgEl(target as HTMLImageElement);
                        } else {
                          setSelectedImgEl(null);
                        }
                      }}
                      className="min-h-[540px] max-h-[680px] overflow-y-auto p-6 text-ink-800 focus:outline-none prose prose-sm max-w-none"
                      style={{
                        fontFamily:
                          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                        lineHeight: 1.6,
                      }}
                    />
                  ) : (
                    <textarea
                      ref={codeTextareaRef}
                      value={htmlBody}
                      onFocus={() => {
                        lastFocusedTargetRef.current = 'body';
                      }}
                      onChange={(e) => {
                        setHtmlBody(e.target.value);
                        setIsDirty(true);
                      }}
                      spellCheck={false}
                      className="min-h-[540px] w-full flex-1 resize-y bg-slate-950 p-4 font-mono text-xs leading-relaxed text-slate-100 focus:outline-none"
                      placeholder="E-posta HTML kodunu buraya yazın..."
                    />
                  )}
                </div>

                {/* SAG: CANLI ONIZLEME (LIVE PREVIEW) */}
                {showLivePreview && (
                  <div className="lg:col-span-6 flex flex-col rounded-xl border border-ink-200 bg-white shadow-xs overflow-hidden">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-200 bg-ink-50 px-3 py-2">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-800">
                        <Eye className="h-4 w-4 text-blue-600" />
                        <span>Canlı E-Posta Önizleme</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-ink-700 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={useSampleData}
                            onChange={(e) => setUseSampleData(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-ink-300 text-ink-900"
                          />
                          <span>Örnek Verilerle Doldur</span>
                        </label>

                        <div className="flex items-center gap-0.5 rounded-lg border border-ink-200 bg-white p-0.5">
                          <button
                            type="button"
                            onClick={() => setPreviewDevice('desktop')}
                            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition ${
                              previewDevice === 'desktop'
                                ? 'bg-ink-900 text-white'
                                : 'text-ink-600 hover:bg-ink-100'
                            }`}
                            title="Masaüstü Görünümü"
                          >
                            <Monitor className="h-3.5 w-3.5" />
                            Masaüstü
                          </button>
                          <button
                            type="button"
                            onClick={() => setPreviewDevice('mobile')}
                            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition ${
                              previewDevice === 'mobile'
                                ? 'bg-ink-900 text-white'
                                : 'text-ink-600 hover:bg-ink-100'
                            }`}
                            title="Mobil Görünümü (375px)"
                          >
                            <Smartphone className="h-3.5 w-3.5" />
                            Mobil
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* E-posta İstemcisi Başlık Simülasyonu */}
                    <div className="border-b border-ink-100 bg-white px-4 py-2.5 text-xs">
                      <div className="flex items-center gap-2 text-ink-500">
                        <span className="font-medium text-ink-700">Konu:</span>
                        <span className="font-semibold text-ink-900 truncate">
                          {previewSubject}
                        </span>
                      </div>
                    </div>

                    {/* Iframe Onizleme Alani */}
                    <div className="flex-1 bg-slate-100 p-3 flex justify-center overflow-auto min-h-[540px]">
                      <div
                        className={`bg-white shadow-sm rounded-lg overflow-hidden transition-all duration-200 ${
                          previewDevice === 'mobile' ? 'w-[375px]' : 'w-full'
                        }`}
                      >
                        <iframe
                          title="E-Posta Canlı Önizleme"
                          srcDoc={previewHtml}
                          sandbox="allow-same-origin"
                          className="w-full h-[600px] border-0"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-ink-200 bg-white p-12 text-center text-sm text-ink-500">
              Düzenlemek için sol listeden bir e-posta şablonu seçin.
            </div>
          )}
        </div>
      </div>

      {/* ================= MODAL: RESIM / LOGO YUKLE & EKLE ================= */}
      <Modal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        title="Şablona Resim / Logo / Banner Ekle"
      >
        <div className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="hidden"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) void handleImageFileUpload(file);
            }}
          />

          {/* Bilgisayardan Yukleme Alani */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOverImg(true);
            }}
            onDragLeave={() => setIsDragOverImg(false)}
            onDrop={handleImageDrop}
            onClick={() => !isUploadingImg && fileInputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition ${
              isDragOverImg
                ? 'border-blue-500 bg-blue-50/60'
                : 'border-ink-300 bg-ink-50/40 hover:border-ink-400 hover:bg-ink-50'
            }`}
          >
            {isUploadingImg ? (
              <div className="flex flex-col items-center gap-2 py-2">
                <Spinner className="h-6 w-6 text-blue-600" />
                <p className="text-xs font-medium text-ink-700">Görsel sunucuya yükleniyor...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Upload className="h-5 w-5" />
                </div>
                <p className="text-xs font-semibold text-ink-800">
                  Bilgisayardan Resim Seçin veya Buraya Sürükleyin
                </p>
                <p className="text-[11px] text-ink-500">
                  PNG, JPG, WebP, GIF (Maks. 10 MB) — Otomatik olarak e-posta uyumlu tam bağlantıya dönüştürülür
                </p>
              </div>
            )}
          </div>

          {imgUrl && (
            <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2.5">
              <img
                src={imgUrl}
                alt="Önizleme"
                className="h-14 w-20 rounded border border-ink-200 bg-white object-contain"
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-emerald-900">✓ Görsel Hazır</p>
                <p className="truncate text-[11px] text-ink-500">{imgUrl}</p>
              </div>
            </div>
          )}

          <Field
            label="Görsel URL Adresi"
            hint="Bilgisayardan yüklediğinizde otomatik dolar veya harici bir resim linki yapıştırabilirsiniz."
          >
            <input
              type="text"
              value={imgUrl}
              onChange={(e) => setImgUrl(e.target.value)}
              placeholder="https://..."
              className="input"
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Görsel Boyutu (Genişlik)">
              <select
                value={imgWidth}
                onChange={(e) => setImgWidth(e.target.value)}
                className="input"
              >
                <option value="100%">Tam Genişlik (%100 — Banner)</option>
                <option value="450px">Büyük (450px)</option>
                <option value="280px">Orta (280px)</option>
                <option value="160px">Logo Boyutu (160px)</option>
                <option value="96px">Küçük İkon / Rozet (96px)</option>
              </select>
            </Field>

            <Field label="Hizalama">
              <select
                value={imgAlign}
                onChange={(e) => setImgAlign(e.target.value as 'left' | 'center' | 'right')}
                className="input"
              >
                <option value="center">Ortala</option>
                <option value="left">Sola Yasla</option>
                <option value="right">Sağa Yasla</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Alternatif Metin (Alt Text)">
              <input
                type="text"
                value={imgAlt}
                onChange={(e) => setImgAlt(e.target.value)}
                placeholder="Örn: Hurmahane Kampanya"
                className="input"
              />
            </Field>

            <Field label="Tıklanınca Açılacak Link (Opsiyonel)">
              <input
                type="text"
                value={imgLinkUrl}
                onChange={(e) => setImgLinkUrl(e.target.value)}
                placeholder="Örn: {StorefrontUrl} veya https://..."
                className="input"
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-xs text-ink-700 cursor-pointer">
            <input
              type="checkbox"
              checked={imgRounded}
              onChange={(e) => setImgRounded(e.target.checked)}
              className="h-4 w-4 rounded border-ink-300 text-ink-900"
            />
            <span>Köşeleri yuvarlat (Modern görünüm)</span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowImageModal(false)}
              className="btn-secondary"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleInsertImageConfirm}
              className="btn-primary"
            >
              Şablona Ekle
            </button>
          </div>
        </div>
      </Modal>

      {/* ================= MODAL: BUTON (CTA) EKLE ================= */}
      <Modal
        open={showButtonModal}
        onClose={() => setShowButtonModal(false)}
        title="Şablona E-Posta Butonu (CTA) Ekle"
      >
        <div className="space-y-4">
          <Field label="Buton Üzerindeki Yazı">
            <input
              type="text"
              value={btnText}
              onChange={(e) => setBtnText(e.target.value)}
              placeholder="Örn: SİPARİŞİMİ TAKİP ET"
              className="input"
            />
          </Field>

          <Field
            label="Buton Bağlantı Adresi (URL veya Değişken)"
            hint="Örn: {OrderTrackingUrl}, {ResetUrl}, {StorefrontUrl} veya https://hurmahane.com"
          >
            <input
              type="text"
              value={btnUrl}
              onChange={(e) => setBtnUrl(e.target.value)}
              className="input font-mono text-xs"
            />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Buton Rengi">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={btnBgColor}
                  onChange={(e) => setBtnBgColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-ink-200"
                />
                <input
                  type="text"
                  value={btnBgColor}
                  onChange={(e) => setBtnBgColor(e.target.value)}
                  className="input font-mono text-xs"
                />
              </div>
            </Field>

            <Field label="Yazı Rengi">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={btnTextColor}
                  onChange={(e) => setBtnTextColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-ink-200"
                />
                <input
                  type="text"
                  value={btnTextColor}
                  onChange={(e) => setBtnTextColor(e.target.value)}
                  className="input font-mono text-xs"
                />
              </div>
            </Field>

            <Field label="Hizalama">
              <select
                value={btnAlign}
                onChange={(e) => setBtnAlign(e.target.value as 'left' | 'center' | 'right')}
                className="input"
              >
                <option value="center">Ortala</option>
                <option value="left">Sola Yasla</option>
                <option value="right">Sağa Yasla</option>
              </select>
            </Field>
          </div>

          <div className="rounded-lg border border-ink-200 bg-ink-50 p-4 text-center">
            <span
              style={{
                display: 'inline-block',
                backgroundColor: btnBgColor,
                color: btnTextColor,
                fontSize: '14px',
                fontWeight: 600,
                padding: '12px 28px',
                borderRadius: '10px',
              }}
            >
              {btnText || 'BUTON ÖNİZLEME'}
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowButtonModal(false)}
              className="btn-secondary"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleInsertButtonConfirm}
              className="btn-primary"
            >
              Butonu Ekle
            </button>
          </div>
        </div>
      </Modal>

      {/* ================= MODAL: BILGI / UYARI / KUPON KUTUSU EKLE ================= */}
      <Modal
        open={showCalloutModal}
        onClose={() => setShowCalloutModal(false)}
        title="Bilgi, Uyarı veya Kupon Kutusu Ekle"
      >
        <div className="space-y-4">
          <Field label="Kutu Tipi">
            <select
              value={calloutTone}
              onChange={(e) =>
                setCalloutTone(
                  e.target.value as 'info' | 'success' | 'warning' | 'danger' | 'coupon',
                )
              }
              className="input"
            >
              <option value="info">Mavi Bilgi Kutusu</option>
              <option value="success">Yeşil Onay / Başarı Kutusu</option>
              <option value="warning">Sarı Önemli Uyarı Kutusu</option>
              <option value="danger">Kırmızı Güvenlik / Kritik Uyarı Kutusu</option>
              <option value="coupon">Kesikli Çizgili Kupon / Kod Kutusu</option>
            </select>
          </Field>

          <Field label="Kutu Başlığı">
            <input
              type="text"
              value={calloutTitle}
              onChange={(e) => setCalloutTitle(e.target.value)}
              className="input"
            />
          </Field>

          <Field label={calloutTone === 'coupon' ? 'Kupon Kodu veya Değişken' : 'Kutu İçerik Metni'}>
            <textarea
              rows={3}
              value={calloutBody}
              onChange={(e) => setCalloutBody(e.target.value)}
              className="input"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowCalloutModal(false)}
              className="btn-secondary"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleInsertCalloutConfirm}
              className="btn-primary"
            >
              Kutuyu Ekle
            </button>
          </div>
        </div>
      </Modal>

      {/* ================= MODAL: TEST E-POSTASI GONDER ================= */}
      <Modal
        open={showTestModal}
        onClose={() => setShowTestModal(false)}
        title={`"${selectedTemplate?.name ?? ''}" Şablonunu Test Gönder`}
      >
        <div className="space-y-4">
          <p className="text-xs text-ink-600 leading-relaxed">
            Şu an editörde açık olan tasarım (kaydetmemiş olsanız bile) örnek sipariş/müşteri verileriyle doldurularak aşağıdaki e-posta adresine gerçek e-posta olarak gönderilecektir.
          </p>

          <Field label="Alıcı E-Posta Adresi" required>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="ornek@alanadi.com"
              className="input"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowTestModal(false)}
              className="btn-secondary"
            >
              Vazgeç
            </button>
            <button
              type="button"
              disabled={testEmailMutation.isPending || !testEmail.trim()}
              onClick={() => testEmailMutation.mutate()}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              {testEmailMutation.isPending ? (
                <Spinner className="h-4 w-4 text-white" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Test E-Postası Gönder
            </button>
          </div>
        </div>
      </Modal>

      {/* ================= MODAL: YENI SABLON OLUSTUR ================= */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Yeni Özel E-Posta Şablonu Oluştur"
      >
        <div className="space-y-4">
          <Field
            label="Şablon Adı"
            required
            hint="Örn: Özel Kampanya Duyurusu"
          >
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Şablon adı..."
              className="input"
            />
          </Field>

          <Field
            label="Şablon Anahtarı (Key)"
            hint="Boş bırakırsanız şablon adından otomatik üretilir. Örn: campaign.ramadan"
          >
            <input
              type="text"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="custom.kampanya"
              className="input font-mono text-xs"
            />
          </Field>

          <Field label="Varsayılan Konu Başlığı" required>
            <input
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              className="input"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="btn-secondary"
            >
              Vazgeç
            </button>
            <button
              type="button"
              disabled={createMutation.isPending || !newName.trim() || !newSubject.trim()}
              onClick={() => createMutation.mutate()}
              className="btn-primary"
            >
              Oluştur ve Düzenle
            </button>
          </div>
        </div>
      </Modal>

      {/* ================= ONAY DIALOGLARI ================= */}
      <ConfirmDialog
        open={confirmReset}
        title="Varsayılan Tasarıma Sıfırla"
        message={`"${selectedTemplate?.name ?? ''}" şablonunda yaptığınız tüm özel HTML ve konu değişiklikleri silinerek sistemin ilk fabrika tasarımına geri dönülecek. Onaylıyor musunuz?`}
        confirmLabel="Evet, Varsayılana Sıfırla"
        danger
        busy={resetMutation.isPending}
        onCancel={() => setConfirmReset(false)}
        onConfirm={() => resetMutation.mutate()}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Özel Şablonu Sil"
        message={`"${selectedTemplate?.name ?? ''}" şablonunu kalıcı olarak silmek istediğinize emin misiniz?`}
        confirmLabel="Evet, Sil"
        danger
        busy={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}
